import { config } from '@iheartjane/config';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import { createContext, useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type { Channel } from 'stream-chat';
import { StreamChat } from 'stream-chat';
import { LoadingIndicator } from 'stream-chat-react';

interface Store {
  name: string;
  photo: string;
}

interface User {
  first_initial: string;
}

interface Conversation {
  channel: Channel;
  store: Store;
  user: User;
}

const ConversationContext = createContext<Conversation | null>(null);
ConversationContext.displayName = 'Conversation';

export const useConversationContext = () =>
  useContextEnsuringProvider(ConversationContext);

export const ConversationProvider = ({
  conversationId,
  children,
}: PropsWithChildren<{ conversationId: string }>) => {
  const [error, setError] = useState(false);
  const [conversation, setConversation] = useState<Conversation>();
  const chatClient = StreamChat.getInstance(config.streamChatApiToken);

  useEffect(() => {
    fetch(`${config.apiPath}/reservations/${conversationId}/chat`)
      .then((res) => {
        if (res.status === 404) {
          throw new Error('Not found');
        }
        return res.json();
      })
      .then(({ chat_user, conversation: streamConversation, store, user }) => {
        chatClient.connectUser(
          {
            id: chat_user.id,
          },
          chat_user.token
        );
        const channel = chatClient.channel('messaging', streamConversation.id);
        setConversation({
          store,
          user,
          channel,
        });
      })
      .catch(() => {
        setError(true);
      });

    return () => {
      chatClient.disconnectUser();
    };
  }, [conversationId, chatClient]);

  if (error) {
    return <p>Unable to connect to chat. Please contact the store.</p>;
  }

  if (!conversation) {
    return <LoadingIndicator size={25} />;
  }

  return (
    <ConversationContext.Provider value={conversation}>
      {children}
    </ConversationContext.Provider>
  );
};
